import * as React from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as LocksState from "../../store/Locks";
import SelectLock from "../shared/SelectLock";

import "./adminSites.css";
import { confirmPrompt } from "../../helpers";
import SelectSite from "../shared/SelectSite";
import {
  AccessControlDoorType,
  KeyRequestCredentialType,
  LockResponse,
  SiteResponse,
  SiteSystemType,
} from "../../api/ApiClient";
import Modal from "components/shared/Modal";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { Button } from "components/ui/button";
import { useSnackbar } from "notistack";
import {
  LOCKS_VIEW,
  setLocksViewPreference,
} from "shared/utils/locksViewPreference";
import { isSuperUser } from "shared/api/isSuperUser";
import { ConnectUserResponse } from "api/ApiClient";

interface Injected {
  locks: LocksState.LocksState;
}

interface SnackProps {
  enqueueSnackbar: (node: React.ReactNode, options?: any) => void;
  closeSnackbar: (key?: any) => void;
}

type Props = Injected & typeof LocksState.actionCreators & SnackProps;

type UserInjected = {
  meUser?: ConnectUserResponse;
};

type ModalType = "lock" | null;

interface State {
  inProgress: boolean;
  showingModal: ModalType;
  syncMenuOpen: boolean;
  //lock: LockResponse | null;
}

class AdminLocks extends React.Component<Props & UserInjected, State> {
  state: State = {
    inProgress: false,
    showingModal: null,
    syncMenuOpen: false,
    //lock: null
  };

  showAddLockModal = () => {
    this.setState({ showingModal: "lock" });
  };

  closeModal = () => {
    this.setState({ showingModal: null });
  };

  onRegistered = async () => {
    this.closeModal();
    this.props.requestLocks();
  };

  renderRegisterLock = () => {
    //@ts-ignore
    return <RegisterLock onRegistered={this.onRegistered} />;
  };

  public render() {
    const { showingModal } = this.state;

    const button = (
      <Button
        type="button"
        onClick={this.showAddLockModal}
      >
        Add Lock
      </Button>
    );

    return (
      <div>
        <div className="mb-4 border-b-2 border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-medium leading-6 text-gray-900">
              Locks
            </h3>
            {isSuperUser(this.props.meUser as any) ? (
              <Link
                to="/admin/locks"
                onClick={() => setLocksViewPreference(LOCKS_VIEW.NEW)}
                className="text-xs text-muted-foreground underline"
              >
                Switch to new view
              </Link>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end">{button}</div>

        <div className="mt-4">{this.renderLocksTable()}</div>

        <Modal
          show={!!showingModal}
          onHide={this.closeModal}
          aria-labelledby="ModalHeader"
          dialogClassName="custom-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title id="ModalHeader">
              {showingModal === "lock" ? "Add Lock" : "Edit Lock"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {showingModal === "lock" ? (
              this.renderRegisterLock()
            ) : (
              <React.Fragment />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Dismiss>Close</Modal.Dismiss>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  deleteLock = (lock: LockResponse) => {
    confirmPrompt({
      promptMessage: "Are you sure you want to delete this Lock?",
      onConfirm: async () => {
        try {
          await this.props.deleteLock({
            id: lock.id || "",
          });
          await this.props.requestLocks();
        } catch (e) {
          let errorMessage = "Error deleting lock";
          try {
            const err: any = e;
            if (err?.response) {
              const data =
                typeof err.response === "string"
                  ? JSON.parse(err.response)
                  : err.response;
              if (data?.errors?.length) {
                errorMessage = data.errors
                  .map((x: any) => x.message)
                  .join(", ");
              } else if (data?.message) {
                errorMessage = data.message;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch {
            // swallow parse error
          }
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              {errorMessage}
            </p>,
            { variant: "error" },
          );
        }
      },
    });
  };

  toggleFlag = (flag: number) => {
    const flags = this.props.locks.flags ? this.props.locks.flags : 0;
    if (flags && flags & flag) {
      this.props.editSyncFlags(flags - flag);
    } else {
      this.props.editSyncFlags(flags | flag);
    }
  };

  syncLock = (lockId: string) => {
    this.props.syncLock(lockId, this.props.locks.flags);
  };

  getFlags = () => {
    const flags = this.props.locks.flags || 0;
    return [
      { id: 1, name: "Audits", enabled: (flags & 1) === 1 },
      { id: 2, name: "Config", enabled: (flags & 2) === 2 },
      { id: 4, name: "Database", enabled: (flags & 4) === 4 },
    ];
  };

  private renderLockButton = (lock: LockResponse) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Button
          onClick={() => this.deleteLock(lock)}
          type="button"
          variant="destructive"
        >
          Delete
        </Button>
        <Button asChild>
          <Link to={`/admin/locks-legacy/${lock.id}/edit`}>Edit</Link>
        </Button>
        {lock &&
        lock.site &&
        lock.site.account &&
        lock.site.account.systemType === SiteSystemType.SchlageEngage ? (
          <React.Fragment>
            {lock.lastSeenTimeStamp && (
              <DropdownMenu
                open={this.state.syncMenuOpen}
                onOpenChange={(syncMenuOpen) => this.setState({ syncMenuOpen })}
              >
                <div className="inline-flex">
                  <Button
                    className="rounded-r-none"
                    disabled={this.props.locks.syncLockInProgress}
                    onClick={() => this.syncLock(lock.id as string)}
                    size="sm"
                    type="button"
                  >
                    {this.props.locks.syncLockInProgress
                      ? "Syncing..."
                      : "Sync"}
                  </Button>
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label="Choose lock sync options"
                      className="rounded-l-none border-l border-l-primary-foreground/30"
                    disabled={this.props.locks.syncLockInProgress}
                      onClick={(event) => event.stopPropagation()}
                      size="icon-sm"
                      type="button"
                    >
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                </div>
                <DropdownMenuContent align="end">
                  {this.getFlags().map((item) => (
                    <DropdownMenuCheckboxItem
                      checked={item.enabled}
                      key={item.id}
                      onSelect={(event) => {
                        event.preventDefault();
                        this.toggleFlag(item.id);
                      }}
                    >
                      {item.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button asChild>
              <Link to={`/admin/locks-legacy/${lock.id}/events`}>Events</Link>
            </Button>
            <Button asChild>
              <Link to={`/admin/locks-legacy/${lock.id}/automation`}>
                Automation
              </Link>
            </Button>
          </React.Fragment>
        ) : (
          <React.Fragment />
        )}
      </div>
    );
  };

  private renderLocksTable() {
    return (
      <div>
        {
          //@ts-ignore
          <SelectLock
            pageSize={this.props.locks.pageSize}
            renderButton={this.renderLockButton}
          />
        }
      </div>
    );
  }
}

const ConnectedAdminLocks = connect<any, any, any, ApplicationState>(
  (state) => ({
    locks: state.locks,
    meUser: state.me.user,
  }),
  {
    ...LocksState.actionCreators,
  },
)(AdminLocks);
const AdminLocksWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedAdminLocks
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default AdminLocksWithSnackbar;

type RegisterLockOwnProps = {
  lock: LockResponse;
  onRegistered: () => any;
};

type RegisterLockInjected = typeof LocksState.actionCreators;

type RegisterLockProps = RegisterLockInjected &
  typeof LocksState.actionCreators &
  RegisterLockOwnProps &
  SnackProps;

interface RegisterLockState {
  inProgress: boolean;

  //create lock
  externalId: string;
  name: string;
  systemLockId: string;
  credentialType: KeyRequestCredentialType;
  doorType: AccessControlDoorType;

  //select Site
  selectedSite: {
    id?: string;
    name?: string;
  } | null;
  selectingSite: boolean;
}

class RegisterLockComp extends React.Component<
  RegisterLockProps,
  RegisterLockState
> {
  state: RegisterLockState = {
    inProgress: false,

    //create lock
    externalId: "",
    name: "",
    systemLockId: "",
    doorType: AccessControlDoorType.Guest,
    credentialType: KeyRequestCredentialType.MobileKey,

    selectedSite: null,
    selectingSite: false,
  };

  private renderSelectSiteButton = (site: SiteResponse) => {
    return (
      <div>
        <button
          type="button"
          className={`btn btn-primary`}
          onClick={() =>
            this.setState({ selectingSite: false, selectedSite: site })
          }
        >
          Select
        </button>
        <p />
      </div>
    );
  };

  renderSelectSite = () => {
    const { selectedSite, selectingSite } = this.state;
    return (
      <div>
        {(!selectedSite || selectingSite) && (
          <>
            <h3>Please Select a Site</h3>
            {
              //@ts-ignore
              <SelectSite
                pageSize={5}
                renderButton={this.renderSelectSiteButton}
              />
            }
          </>
        )}
        {selectedSite && (
          <>
            <div>
              Selected Site:{" "}
              <b>
                {selectedSite.id} {selectedSite.name}
              </b>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                this.setState({ selectingSite: true, selectedSite: null })
              }
            >
              Change Site
            </Button>
            <p />
          </>
        )}
      </div>
    );
  };

  register = (e: any) => {
    e.preventDefault();

    this.setState(
      {
        inProgress: true,
      },
      async () => {
        const state = this.state;
        try {
          const json = (await this.props.registerLock({
            siteId: state.selectedSite ? state.selectedSite.id || "" : "",
            name: state.name,
            systemLockId: state.systemLockId,
            doorType: state.doorType,
          })) as any;
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              Successfully registered lock: {json?.name || json?.id}
            </p>,
            { variant: "success" },
          );
          this.props.onRegistered();
          this.setState({
            //clear inputs
            externalId: "",
            name: "",
            systemLockId: "",
            doorType: AccessControlDoorType.Guest,
            credentialType: KeyRequestCredentialType.MobileKey,
          });
        } catch (e) {
          let errorMessage = "Error registering lock";
          try {
            const err: any = e;
            if (err?.response) {
              const data =
                typeof err.response === "string"
                  ? JSON.parse(err.response)
                  : err.response;
              if (data?.errors?.length) {
                errorMessage = data.errors
                  .map((x: any) => x.message)
                  .join(", ");
              } else if (data?.message) {
                errorMessage = data.message;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch {
            // ignore parse issues
          }
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              {errorMessage}
            </p>,
            { variant: "error" },
          );
        } finally {
          this.setState({
            inProgress: false,
          });
        }
      },
    );
  };

  render() {
    const { inProgress, name, systemLockId, doorType } = this.state;

    return (
      <form>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            className="form-control"
            name="name"
            placeholder="Name"
            value={name}
            onChange={(e) => {
              this.setState({ name: e.target.value });
            }}
          />
        </div>

        {this.renderSelectSite()}

        <div className="form-group">
          <label htmlFor="systemLockId">System Lock Id *</label>
          <input
            className="form-control"
            name="systemLockId"
            placeholder="SystemLockId"
            value={systemLockId}
            onChange={(e) => {
              this.setState({ systemLockId: e.target.value });
            }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="doorType">Door Type</label>
          <select
            className="form-control"
            name="doorType"
            value={doorType}
            onChange={(e) => {
              let doorType = e.target.value as AccessControlDoorType;
              this.setState({ doorType: doorType });
            }}
          >
            {Object.keys(AccessControlDoorType)
              .filter((k) => isNaN(Number(k)))
              .map((k, i) => (
                <option key={k} value={i}>
                  {k}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Button
            type="submit"
            disabled={!!inProgress}
            onClick={this.register}
          >
            Register Lock
          </Button>
        </div>
      </form>
    );
  }
}

const ConnectedRegisterLock = connect<
  any,
  any,
  RegisterLockOwnProps,
  ApplicationState
>(
  (state) => ({
    locks: state.locks,
  }),
  LocksState.actionCreators,
)(RegisterLockComp);

const RegisterLock: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedRegisterLock
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};
